import { FormControl, Autocomplete, Box, TextField, CircularProgress } from '@mui/material';
import Dot from 'components/common/dot/Dot';
import UserAvatar from 'components/common/userAvatar/UserAvatar';
import { useSearchItemQuery } from 'hooks/useSearchQuery';
import useUserData, { UserType } from 'hooks/useUserData';
import { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import useCompanyQueryData from 'hooks/useCompanyQueryData';
import { PostFormData } from '@buildwise/pages/addPost/hooks/useAddPostForm';
import { uniqBy, uniqueId } from 'lodash';
import { useSuggestionContactsQuery } from '@buildwise/libs/company-admin';
import { useLocation } from 'react-router-dom';

type UserOptionType = {
  id: string;
  primaryCompanyName?: string;
  profileImageURL?: string;
  userNameToDisplay: string;
  userPrimaryCompanyName?: string;
  title: string;
  username: string;
} & {
  label: any;
  value: string;
};

type UsersSearchResponseType = {
  totalElements?: number;
  totalNumber?: number;
  totalPages?: number;
  userResponse: UserType[];
};

type QuestionAssignedToFieldProps = {
  autoFocus?: boolean;
  departments: string[];
  regions: string[];
};

function QuestionAssignedToField({
  autoFocus,
  departments,
  regions,
}: QuestionAssignedToFieldProps) {
  const { shouldDisplayCompanyOnUserCards } = useCompanyQueryData();
  const { user, companyId } = useUserData();
  const { pathname } = useLocation();
  const { suggestionContacts, isLoading } = useSuggestionContactsQuery({
    companyId,
    shouldFetch: !pathname.includes('suggestion-contacts'),
  });
  const {
    control,
    formState: { isSubmitting },
    setValue,
  } = useFormContext<PostFormData>();
  const { data: peopleData, isLoading: peopleLoading } =
    useSearchItemQuery<UsersSearchResponseType>(
      {
        searchType: 'users',
        operator: 'or',
        filterBy: {
          filters: [
            {
              filterType: 'name',
              filterValue: 'ALL',
            },
          ],
        },
      },
      pathname.includes('suggestion-contacts'),
    );
  const peopleOptions = useMemo(
    () =>
      pathname.includes('suggestion-contacts')
        ? (peopleData?.userResponse || [])
            .filter(
              (_user) =>
                (departments.length === 0 && regions.length === 0) ||
                (!_user.department && !_user.region) ||
                (_user.department && departments.includes(_user.department)) ||
                (_user.region && regions.includes(_user.region)),
            )
            .map((userData) => ({
              ...userData,
              label: `${userData?.userNameToDisplay} - ${userData?.email}`,
              value: userData.username,
            }))
        : uniqBy(
            suggestionContacts
              .filter(
                (contact) =>
                  (departments.length === 0 && regions.length === 0) ||
                  departments.includes(contact.groupName) ||
                  regions.includes(contact.region),
              )
              .map((contact) => ({
                id: contact.id,
                primaryCompanyName: contact.userPrimaryCompanyName,
                profileImageURL: contact.userProfileImageURL,
                userNameToDisplay: contact.userNameToDisplay,
                title: contact.userTitleToDisplay,
                username: contact.owner,
                label: `${contact.userNameToDisplay} - ${contact.owner}`,
                value: contact.owner,
              })),
            'value',
          ),
    [suggestionContacts, departments, regions, peopleData, pathname],
  );
  const taskOwner =
    useWatch({
      control,
    })?.taskOwner || null;
  const [fieldValue, setFieldValue] = useState<UserOptionType | null>(
    taskOwner
      ? {
          id: uniqueId(),
          primaryCompanyName: '',
          profileImageURL: '',
          userNameToDisplay: taskOwner,
          userPrimaryCompanyName: '',
          title: '',
          username: taskOwner,
          label: taskOwner,
          value: taskOwner,
        }
      : null,
  );

  const handleSelectUsers = useCallback(
    (_: SyntheticEvent<Element, Event>, user: UserOptionType | null) => {
      setValue('taskOwner', user?.value || '', { shouldValidate: true });
      setFieldValue(user);
    },
    [],
  );

  return (
    <Controller
      control={control}
      name="taskOwner"
      render={({ field: { value, ref, ...rest }, fieldState }) => (
        <FormControl fullWidth>
          <Autocomplete
            {...rest}
            id="taskOwner"
            onChange={handleSelectUsers}
            value={fieldValue}
            options={peopleOptions}
            filterOptions={(options, state) =>
              options.filter(
                (option) =>
                  option.userNameToDisplay.toLowerCase().includes(state.inputValue.toLowerCase()) ||
                  option.username.toLowerCase().includes(state.inputValue.toLowerCase()),
              )
            }
            fullWidth
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <UserAvatar
                  disableUserLink
                  avatarSize={40}
                  profileImageURL={option.profileImageURL}
                  title={`${option.userNameToDisplay} (${option.username})`}
                  avatarName={option.userNameToDisplay}
                  subTitle={
                    <>
                      {option.title}
                      {shouldDisplayCompanyOnUserCards &&
                        (option?.primaryCompanyName || option?.userPrimaryCompanyName) && (
                          <>
                            <Dot />
                            {option?.primaryCompanyName || option?.userPrimaryCompanyName}
                          </>
                        )}
                    </>
                  }
                  userName={option.username}
                />
              </Box>
            )}
            autoHighlight
            noOptionsText={isLoading || peopleLoading ? 'Loading...' : 'No Users Found'}
            getOptionDisabled={(option) => option.username === user?.username}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={ref}
                placeholder="i.e. John Doe or john.doe@email.com"
                autoFocus={autoFocus}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading || peopleLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
                variant="standard"
              />
            )}
            disabled={isSubmitting}
          />
        </FormControl>
      )}
    />
  );
}

export default QuestionAssignedToField;
